import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/WizardServicos_CarregaCompromissoInput';
import '../contracts/WizardServicos_EntrouSemProblemasInput';
import '../contracts/WizardServicos_FinalizaCompromissoInput';
import type { WizardServicos_CarregaCompromissoResponse } from '../contracts/WizardServicos_CarregaCompromissoResponse';
import type { WizardServicos_EntrouSemProblemasResponse } from '../contracts/WizardServicos_EntrouSemProblemasResponse';
import type { WizardServicos_FinalizaCompromissoResponse } from '../contracts/WizardServicos_FinalizaCompromissoResponse';
import '../contracts/WizardServicos_CarregaCompromissoResponse';
import '../contracts/WizardServicos_EntrouSemProblemasResponse';
import '../contracts/WizardServicos_FinalizaCompromissoResponse';

import '../contracts/ImagensServicos_BuscaFotosInput';
import '../contracts/ImagensServicos_BuscaFotosResponse';
import '../contracts/ImagensServicos_DeletarFotoAjaxInput';
import '../contracts/ImagensServicos_DeletarFotoAjaxResponse';
import '../contracts/ImagensServicos_UploadFotoAjaxInput';
import '../contracts/ImagensServicos_UploadFotoAjaxResponse';
import type { ImagensServicos_BuscaFotosResponse } from '../contracts/ImagensServicos_BuscaFotosResponse';
interface ApiResponse<T = unknown> {
  success: boolean;
  dados?: T;
  data?: T;
  error?: string;
  message?: string;
  compromisso?: CompromissoData;
  fotos?: FotoRow[];
}

interface ServicoRow {
  compromisso_servico_id?: number;
  compromisso_servico_status?: number;
  compromisso_servico_valor?: number | string;
  tem_imagens_antes?: boolean;
  tem_imagens_depois?: boolean;
  [key: string]: unknown;
}

interface CompromissoData {
  compromisso_id?: number;
  compromisso_entrou_sem_problemas?: number | string;
  servicos?: ServicoRow[];
  [key: string]: unknown;
}

interface FotoRow {
  imagem_id?: number;
  [key: string]: unknown;
}

/**
 * Composable for Wizard Servicos (Service Wizard) operations
 * Provides methods to interact with the Wizard Servicos API
 */
export function useWizardServicos() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Load wizard data for a compromisso (appointment)
   * Includes services and their image status
   */
  const loadWizardData = async (compromissoId: number) => {
    log('useWizardServicos', 'loadWizardData', 'Loading wizard data', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<WizardServicos_CarregaCompromissoResponse>(
        `/index.php/wizard_servicos/carrega_compromisso/${compromissoId}/`,
        null,
        {
          inputContract: 'WizardServicos_CarregaCompromissoInput',
          responseContract: 'WizardServicos_CarregaCompromissoResponse'
        }
      );
      log('useWizardServicos', 'loadWizardData', 'Response', response);

      if (response.success) {
        return response.dados?.compromisso;
      } else {
        throw new Error(response.error || 'Erro ao carregar dados do wizard');
      }
    } catch (err) {
      log('useWizardServicos', 'loadWizardData', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Mark that operator arrived without problems
   * Moves wizard to step 1 (Before photos)
   */
  const markNoProblems = async (compromissoId: number) => {
    log('useWizardServicos', 'markNoProblems', 'Marking no problems', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<WizardServicos_EntrouSemProblemasResponse>(
        `/index.php/wizard_servicos/entrou_sem_problemas/${compromissoId}/`,
        null,
        { inputContract: 'WizardServicos_EntrouSemProblemasInput', responseContract: 'WizardServicos_EntrouSemProblemasResponse' }
      );
      log('useWizardServicos', 'markNoProblems', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao marcar entrada sem problemas');
      }
    } catch (err) {
      log('useWizardServicos', 'markNoProblems', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Upload photo for a service
   * @param servicoId - compromisso_servico_id
   * @param file - Image file
   * @param antesDepois - 'a' for before, 'd' for after
   */
  const uploadPhoto = async (servicoId: number, file: File, antesDepois: 'a' | 'd') => {
    log('useWizardServicos', 'uploadPhoto', 'Uploading photo', { servicoId, antesDepois });
    isLoading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('imagem_nome', file);

      const response = await httpService.post<ApiResponse>(
        `/index.php/config/imagens_servicos/upload_foto_ajax/${servicoId}/${antesDepois}/`,
        formData,
        { responseContract: 'ImagensServicos_UploadFotoAjaxResponse', inputContract: 'ImagensServicos_UploadFotoAjaxInput' }
      );
      log('useWizardServicos', 'uploadPhoto', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Erro ao fazer upload da foto');
      }
    } catch (err) {
      log('useWizardServicos', 'uploadPhoto', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Load photos for a service
   * @param servicoId - compromisso_servico_id
   * @param antesDepois - 'a' for before, 'd' for after
   */
  const loadPhotos = async (servicoId: number, antesDepois: 'a' | 'd') => {
    log('useWizardServicos', 'loadPhotos', 'Loading photos', { servicoId, antesDepois });
    isLoading.value = true;
    error.value = null;

    try {
      // Tipar com o contrato gerado (e não `ApiResponse` cru): sem o parâmetro
      // de tipo, `dados` resolve para `{}` e `dados.fotos` não compila.
      const response = await httpService.post<ImagensServicos_BuscaFotosResponse>(
        `/index.php/config/imagens_servicos/busca_fotos/${servicoId}/${antesDepois}/`,
        null,
        {
          inputContract: 'ImagensServicos_BuscaFotosInput',
          responseContract: 'ImagensServicos_BuscaFotosResponse',
        }
      );
      log('useWizardServicos', 'loadPhotos', 'Response', response);

      if (response.success) {
        return response.dados?.fotos || [];
      } else {
        throw new Error(response.error || 'Erro ao carregar fotos');
      }
    } catch (err) {
      log('useWizardServicos', 'loadPhotos', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a photo
   * @param imagemId - Image ID to delete
   */
  const deletePhoto = async (imagemId: number) => {
    log('useWizardServicos', 'deletePhoto', 'Deleting photo', imagemId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<ApiResponse>(
        `/index.php/config/imagens_servicos/deletar_foto_ajax/${imagemId}/`,
        null,
        { inputContract: 'ImagensServicos_DeletarFotoAjaxInput', responseContract: 'ImagensServicos_DeletarFotoAjaxResponse' }
      );
      log('useWizardServicos', 'deletePhoto', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Erro ao deletar foto');
      }
    } catch (err) {
      log('useWizardServicos', 'deletePhoto', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Finalize wizard - complete the service
   * @param data - Wizard completion data
   */
  const finalizeWizard = async (data: Record<string, unknown>) => {
    log('useWizardServicos', 'finalizeWizard', 'Finalizing wizard', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('WizardServicos_FinalizaCompromissoInput', data);
      const response = await httpService.post<WizardServicos_FinalizaCompromissoResponse>(
        '/index.php/wizard_servicos/finaliza_compromisso/',
        data,
        { responseContract: 'WizardServicos_FinalizaCompromissoResponse', inputContract: 'WizardServicos_FinalizaCompromissoInput' }
      );
      log('useWizardServicos', 'finalizeWizard', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao finalizar compromisso');
      }
    } catch (err) {
      log('useWizardServicos', 'finalizeWizard', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Calculate current wizard stage based on compromisso data
   * @param compromisso - Compromisso object with services
   * @returns Current stage (0-3)
   */
  const calculateStage = (compromisso: CompromissoData | null | undefined): number => {
    if (!compromisso) return 0;

    // Convert to number to handle both string and number values from DB
    const entrouSemProblemas = Number(compromisso.compromisso_entrou_sem_problemas);

    log('useWizardServicos', 'calculateStage', 'Checking stage', {
      entrouSemProblemas,
      raw: compromisso.compromisso_entrou_sem_problemas
    });

    // Step 0: Check-in not started
    if (!entrouSemProblemas || entrouSemProblemas === 0) {
      log('useWizardServicos', 'calculateStage', 'Step 0 - Check-in not started');
      return 0;
    }

    const nonCanceledServices = countNonCanceledServices(compromisso);
    const servicesWithBeforePhotos = countServicesWithBeforePhotos(compromisso);
    const servicesWithAfterPhotos = countServicesWithAfterPhotos(compromisso);

    log('useWizardServicos', 'calculateStage', 'Services count', {
      nonCanceledServices,
      servicesWithBeforePhotos,
      servicesWithAfterPhotos
    });

    // Step 1: Before photos incomplete
    if (entrouSemProblemas === 1 && servicesWithBeforePhotos !== nonCanceledServices) {
      log('useWizardServicos', 'calculateStage', 'Step 1 - Before photos incomplete');
      return 1;
    }

    // Step 2: After photos incomplete
    if (
      entrouSemProblemas === 1 &&
      servicesWithBeforePhotos === nonCanceledServices &&
      servicesWithAfterPhotos !== nonCanceledServices
    ) {
      log('useWizardServicos', 'calculateStage', 'Step 2 - After photos incomplete');
      return 2;
    }

    // Step 3: All photos complete, ready for review
    if (
      entrouSemProblemas === 1 &&
      servicesWithBeforePhotos === nonCanceledServices &&
      servicesWithAfterPhotos === nonCanceledServices
    ) {
      log('useWizardServicos', 'calculateStage', 'Step 3 - Ready for review');
      return 3;
    }

    log('useWizardServicos', 'calculateStage', 'Defaulting to Step 0');
    return 0;
  };

  /**
   * Check if can navigate to a specific step
   * @param currentStep - Current step number
   * @param targetStep - Target step number
   * @param compromisso - Compromisso object
   * @returns Whether navigation is allowed
   */
  const canNavigateToStep = (currentStep: number, targetStep: number, compromisso: CompromissoData): boolean => {
    // Always allow backward navigation
    if (targetStep < currentStep) return true;

    // Same step is always allowed
    if (targetStep === currentStep) return true;

    // Forward navigation requires validation
    if (targetStep > currentStep) {
      // Step 0 → 1: Check-in must be completed
      if (currentStep === 0 && targetStep >= 1) {
        return compromisso.compromisso_entrou_sem_problemas === 1;
      }

      // Step 1 → 2: All services must have "Before" photos
      if (currentStep === 1 && targetStep >= 2) {
        const nonCanceled = countNonCanceledServices(compromisso);
        const withBefore = countServicesWithBeforePhotos(compromisso);
        return withBefore === nonCanceled && nonCanceled > 0;
      }

      // Step 2 → 3: All services must have "After" photos
      if (currentStep === 2 && targetStep >= 3) {
        const nonCanceled = countNonCanceledServices(compromisso);
        const withAfter = countServicesWithAfterPhotos(compromisso);
        return withAfter === nonCanceled && nonCanceled > 0;
      }
    }

    return true;
  };

  /**
   * Helper: Count non-canceled services
   */
  const countNonCanceledServices = (compromisso: CompromissoData | null | undefined): number => {
    if (!compromisso || !compromisso.servicos) return 0;
    return compromisso.servicos.filter((s: ServicoRow) => s.compromisso_servico_status !== 6).length;
  };

  /**
   * Helper: Count services with before photos
   */
  const countServicesWithBeforePhotos = (compromisso: CompromissoData | null | undefined): number => {
    if (!compromisso || !compromisso.servicos) return 0;
    return compromisso.servicos.filter(
      (s: ServicoRow) => s.compromisso_servico_status !== 6 && s.tem_imagens_antes !== false
    ).length;
  };

  /**
   * Helper: Count services with after photos
   */
  const countServicesWithAfterPhotos = (compromisso: CompromissoData | null | undefined): number => {
    if (!compromisso || !compromisso.servicos) return 0;
    return compromisso.servicos.filter(
      (s: ServicoRow) => s.compromisso_servico_status !== 6 && s.tem_imagens_depois !== false
    ).length;
  };

  /**
   * Calculate total value of finalized services
   */
  const calculateTotalValue = (compromisso: CompromissoData | null | undefined): number => {
    if (!compromisso || !compromisso.servicos) return 0;

    return compromisso.servicos
      .filter((s: ServicoRow) => s.tem_imagens_depois !== false && s.compromisso_servico_status !== 6)
      .reduce((total: number, servico: ServicoRow) => {
        return total + parseFloat(String(servico.compromisso_servico_valor || 0));
      }, 0);
  };

  return {
    // State
    isLoading,
    error,

    // API Methods
    loadWizardData,
    markNoProblems,
    uploadPhoto,
    loadPhotos,
    deletePhoto,
    finalizeWizard,

    // Helper Methods
    calculateStage,
    canNavigateToStep,
    countNonCanceledServices,
    countServicesWithBeforePhotos,
    countServicesWithAfterPhotos,
    calculateTotalValue
  };
}
